/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useState } from 'react';

import { omit } from 'lodash';

import useRouter, { useLocation } from 'js/lib/useRouter';

import type { DiscoveryCollection } from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import { useIsLohp } from 'bundles/collections-common/utils/useIsLohp';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import {
  ASK_ANYTHING_AI_PROMPT_ID,
  DEFAULT_COACH_CONFIG,
} from 'bundles/search-common/components/experiments/aiSearchOnLohp/constants';
import SearchBarContent from 'bundles/search-common/components/searchbar/SearchBarContent';
import {
  AUTOCOMPLETE_SECTION_PREFIXES,
  PROMPTS_LIMIT_AS_YOU_TYPE,
  PROMPTS_LIMIT_LOHP_ZERO_STATE,
  PROMPTS_LIMIT_ZERO_STATE,
  autocompletePrefixes,
} from 'bundles/search-common/constants/autocomplete';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import { useAutocompleteSearchConfigs } from 'bundles/search-common/hooks/useAutocompleteSearchConfigs';
import { useAiSearchExperimentData } from 'bundles/search-common/providers/AiSearchExperimentProvider';
import QueryPromptsProvider from 'bundles/search-common/providers/QueryPromptsProvider';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import { saveRecentlySearched } from 'bundles/search-common/utils/SearchUtils';
import {
  type AutocompleteItem,
  type AutocompleteItems,
  getRecentlySearchedAutocompleteItems,
  getRecentlyViewedAutocompleteItems,
  getSearchUpdatedUrl,
  inferAutocompleteMode,
  isExactPartnerNameMatch,
} from 'bundles/search-common/utils/autocomplete';
import { shouldAllowSearchNavigation } from 'bundles/search-common/utils/emptyQueryValidation';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useTrackSearch from 'bundles/search-common/utils/useTrackSearch';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

export type Props = {
  searchIsOpen?: boolean;
  hideMobileSearchPage?: () => void;
  collectionRecommendations?: DiscoveryCollection | null;
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    width: 100%;
  `,

  /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
  searchForm: css`
    margin-bottom: 0;
    width: 100%;
  `,
};

const SearchBarContentWrapper = (props: Props) => {
  const { hideMobileSearchPage, searchIsOpen } = props;
  const router = useRouter();
  const currentQueryParams = router.location?.query;
  const isConsumerSearchPage = useIsConsumerSerp();
  const [searchText, setSearchText] = useState<string>(currentQueryParams?.query || '');
  const [refreshKey, setRefreshKey] = useState(0);
  // Recompute items when refreshKey changes to pick up localStorage updates
  const recentlySearchedItems = React.useMemo(() => {
    // refreshKey is intentionally used to force recomputation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = refreshKey;
    return getRecentlySearchedAutocompleteItems();
  }, [refreshKey]);

  const recentlyViewedItems = React.useMemo(() => {
    // refreshKey is intentionally used to force recomputation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = refreshKey;
    return getRecentlyViewedAutocompleteItems(props.collectionRecommendations);
  }, [refreshKey, props.collectionRecommendations]);

  const [autocompleteMode, setAutocompleteMode] = useState<AutocompleteMode>(() =>
    inferAutocompleteMode(
      searchText,
      getRecentlySearchedAutocompleteItems(),
      getRecentlyViewedAutocompleteItems(props.collectionRecommendations)
    )
  );
  const trackSearch = useTrackSearch();
  const location = useLocation();
  const { setCoachOpen, setContextConfig } = useCoachState();
  const referrer = location.search ? `${location.pathname}${location.search}` : location.pathname;
  const { onboardingUrl } = useBuildOnboardingUrl({ referrer });

  const searchConfigs = useAutocompleteSearchConfigs();

  // Track the debounced query from SearchBarContent — updated only when updateQuery fires
  const [debouncedQuery, setDebouncedQuery] = useState<string>(currentQueryParams?.query || '');

  const handleDebouncedQueryChange = useCallback<(query: string) => void>((query: string) => {
    setDebouncedQuery(query);
  }, []);

  const isLohp = useIsLohp();

  const openCoach = React.useCallback<() => void>(() => {
    setContextConfig({ config: DEFAULT_COACH_CONFIG });
    setCoachOpen(true);
  }, [setCoachOpen, setContextConfig]);

  const { isAiSearchEnabled } = useAiSearchExperimentData();

  const isZeroState = !debouncedQuery.trim();
  const promptsQuery = isZeroState ? undefined : debouncedQuery.trim();
  let promptsLimit = PROMPTS_LIMIT_AS_YOU_TYPE;
  if (isZeroState) {
    promptsLimit = isLohp ? PROMPTS_LIMIT_LOHP_ZERO_STATE : PROMPTS_LIMIT_ZERO_STATE;
  }

  const updateUrl = (searchQuery: string) => {
    let updatedQuery = searchQuery;
    const newQueryParams: Record<string, string> = {};
    // partner matches should set a filter value instead of a query
    if (isExactPartnerNameMatch(searchQuery)) {
      // add partner filter
      newQueryParams.partners = searchQuery;
      // remove query text
      updatedQuery = '';
    }

    const cleanedQueryParams = omit(currentQueryParams, ['page', 'sortBy']);
    const searchUrl = getSearchUpdatedUrl(updatedQuery, { ...cleanedQueryParams, ...newQueryParams });
    if (isConsumerSearchPage) {
      const legacySearchUrl = getSearchUpdatedUrl(updatedQuery, newQueryParams);
      router.push(legacySearchUrl);
    } else {
      // inter-app routing does not currently work so resorting to global builtin
      window.location.assign(searchUrl);
    }
  };

  // fired on selection AND native form submit
  const handleAutocompleteSubmit = (
    rawQuery: string,
    mergedItemsForTracking?: AutocompleteItems,
    selectedItem?: AutocompleteItem
  ) => {
    const sectionIdentifierPrefix = autocompletePrefixes.find((prefix) => rawQuery.startsWith(prefix));
    const queryValue = sectionIdentifierPrefix ? rawQuery.replace(sectionIdentifierPrefix, '') : rawQuery;

    const isAiPromptSelection =
      sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_ZERO_STATE ||
      sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_AS_YOU_TYPE ||
      (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.RECENT_SEARCHES && selectedItem?.isAiPrompt);

    if (isAiPromptSelection) {
      hideMobileSearchPage?.();

      if (rawQuery === ASK_ANYTHING_AI_PROMPT_ID || selectedItem?.routeType === 'CHAT_ONLY') {
        if (rawQuery !== ASK_ANYTHING_AI_PROMPT_ID && selectedItem?.name) {
          saveRecentlySearched(selectedItem.name, {
            isAiPrompt: true,
            routeType: selectedItem.routeType,
            intentType: selectedItem.intentType,
          });
          setRefreshKey((prev) => prev + 1);
        }
        setSearchText('');
        openCoach();
        return;
      }

      if (selectedItem?.routeType === 'SERP') {
        const searchQuery = selectedItem.routeParameter ?? queryValue;
        saveRecentlySearched(selectedItem.name ?? searchQuery, {
          isAiPrompt: true,
          routeType: selectedItem.routeType,
          intentType: selectedItem.intentType,
        });
        setRefreshKey((prev) => prev + 1);
        updateUrl(searchQuery);
        return;
      }

      updateUrl(queryValue);
      return;
    }

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.ONBOARDING && onboardingUrl) {
      trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems);
      hideMobileSearchPage?.();
      window.location.assign(onboardingUrl);
      return;
    }

    // Check if navigation should be allowed for this query
    if (!shouldAllowSearchNavigation(queryValue, currentQueryParams)) {
      // Prevent navigation for organic empty searches
      return;
    }

    // Only track searches that will actually execute
    // Use mergedItemsForTracking if provided (includes recently viewed + trending products from zero state)
    trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems);

    // Hide mobile search page since search is proceeding
    hideMobileSearchPage?.();

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.RECENTLY_VIEWED) {
      // Search in mergedItemsForTracking first (includes trending products), then fall back to recentlyViewedItems
      const itemsToSearch = mergedItemsForTracking || recentlyViewedItems;
      const recentlyViewedItemMatch = itemsToSearch.find((item) => item.name === queryValue);
      if (recentlyViewedItemMatch?.path) {
        window?.location.assign(recentlyViewedItemMatch.path);
        return;
      }
    }

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES) {
      // For zero state trending products, search in mergedItemsForTracking first (includes merged trending products),
      // then fall back to recentlyViewedItems
      // This ensures we find trending products that were merged into LEARNER_FAVORITES or RECENTLY_VIEWED groups
      const itemsToSearch = mergedItemsForTracking || recentlyViewedItems;
      const directMatchItem = itemsToSearch.find((item) => item.name === queryValue || item.textValue === queryValue);
      if (directMatchItem?.path) {
        window?.location.assign(directMatchItem.path);
        return;
      }
    }

    // For all other selections (trending, as you type, etc.), save and navigate to search
    // save to local storage
    saveRecentlySearched(queryValue);
    // Refresh recently searched items to reflect the new search
    setRefreshKey((prev) => prev + 1);
    updateUrl(queryValue);
  };

  const handleNativeFormSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    handleAutocompleteSubmit(searchText);
  };

  const handleInputChange = (query: string) => {
    setSearchText(query);
    setAutocompleteMode(inferAutocompleteMode(query, recentlySearchedItems, recentlyViewedItems));
  };

  // ensure the search text is updated when the url changes (e.g., suggested search click on the SERP)
  React.useEffect(() => {
    setSearchText(currentQueryParams?.query ?? '');
  }, [currentQueryParams?.query]);

  // ensure the menu state is updated when the recently viewed data loads
  React.useEffect(() => {
    setAutocompleteMode(inferAutocompleteMode(searchText, recentlySearchedItems, recentlyViewedItems));
  }, [recentlySearchedItems, recentlyViewedItems, searchText]);

  const handleRefreshItems = React.useCallback<() => void>(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Shared subtree — kept in a variable to avoid duplicating JSX across branches.
  const searchContent = (
    <SearchProvider searchConfigs={searchConfigs} ssr={false} fetchPolicy="no-cache">
      <form className="search-form" role="search" onSubmit={handleNativeFormSubmit} css={styles.searchForm}>
        <SearchBarContent
          {...props}
          searchText={searchText}
          onSubmit={handleAutocompleteSubmit}
          autocompleteMode={autocompleteMode}
          onInputChange={handleInputChange}
          recentlySearchedItems={recentlySearchedItems}
          recentlyViewedItems={recentlyViewedItems}
          onRefreshItems={handleRefreshItems}
          searchIsOpen={searchIsOpen ?? false}
          onDebouncedQueryChange={handleDebouncedQueryChange}
        />
      </form>
    </SearchProvider>
  );

  return (
    <div className="rc-SearchBarContentWrapper" css={styles.wrapper}>
      {/*
       * QueryPromptsProvider is only mounted for experiment participants. This prevents
       * the GraphQL query from ever being registered for control-group users — not just
       * skipped, but never instantiated. useQueryPrompts() falls back to the safe empty
       * context default when the provider is absent.
       */}
      {isAiSearchEnabled ? (
        <QueryPromptsProvider query={promptsQuery} limit={promptsLimit}>
          {searchContent}
        </QueryPromptsProvider>
      ) : (
        searchContent
      )}
    </div>
  );
};

export default SearchBarContentWrapper;
